<template>
  <div class="h-screen bg-background flex theme-transition overflow-hidden">
    <!-- Sidebar -->
    <Sidebar 
      :is-open="sidebarOpen" 
      @close="sidebarOpen = false"
      @navigate="handleNavigation"
    />
    
    <!-- Main content -->
    <div class="flex-1 h-screen flex flex-col relative w-full overflow-hidden">
      <!-- Header -->
      <Header 
        @toggle-sidebar="sidebarOpen = !sidebarOpen"
        @logout="handleLogout"
      />
      
      <!-- Page content -->
      <main class="flex-1 p-4 lg:p-8 overflow-y-auto overflow-x-hidden theme-transition" tabindex="-1">
        <div class="max-w-[1600px] mx-auto w-full">
          <!-- Breadcrumb -->
          <nav v-if="breadcrumbs.length > 0" class="mb-4">
            <ol class="flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest text-text-muted">
              <li>
                <router-link to="/app/dashboard" class="hover:text-primary theme-transition">
                  <HomeIcon class="w-3.5 h-3.5" />
                </router-link>
              </li>
              <li v-for="(crumb, index) in breadcrumbs" :key="index" class="flex items-center">
                <ChevronRightIcon class="w-3 h-3 mx-2 opacity-50" />
                <span v-if="index === breadcrumbs.length - 1" class="text-text-primary">
                  {{ crumb.name }}
                </span>
                <router-link 
                  v-else 
                  :to="crumb.path" 
                  class="hover:text-primary theme-transition"
                >
                  {{ crumb.name }}
                </router-link>
              </li>
            </ol>
          </nav>
          
          <!-- Router view with transition -->
          <router-view v-slot="{ Component, route }">
            <transition 
              name="page" 
              mode="out-in"
              @before-enter="beforeEnter"
              @enter="enter"
              @leave="leave"
            >
              <component 
                :is="Component" 
                :key="route.path"
                class="page-content"
                @error="handleComponentError"
              />
            </transition>
          </router-view>
        </div>
      </main>
    </div>
    
    <!-- Mobile sidebar overlay -->
    <transition name="fade">
      <div 
        v-if="sidebarOpen" 
        class="fixed inset-0 z-30 lg:hidden"
        @click="sidebarOpen = false"
      >
        <div class="fixed inset-0 bg-background/80 backdrop-blur-sm"></div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../../store/auth'
import { useToast } from 'vue-toastification'
import { HomeIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
import Sidebar from './sidebar.vue'
import Header from './Header.vue'

// Composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// State
const sidebarOpen = ref(false)

// Computed
const breadcrumbs = computed(() => {
  const paths = route.path.split('/').filter(Boolean)
  const breadcrumbs = []
  
  let currentPath = ''
  paths.forEach((path, index) => {
    // Skip 'app' prefix in breadcrumbs if present
    if (path === 'app' && index === 0) {
      currentPath = '/app'
      return
    }
    
    currentPath += `/${path}`
    
    // Convert path to readable name
    const name = path
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    
    breadcrumbs.push({
      name,
      path: currentPath
    })
  })
  
  return breadcrumbs
})

// Methods
const handleNavigation = () => {
  if (window.innerWidth < 1024) {
    sidebarOpen.value = false
  }
}

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
  } catch (error) {
    toast.error('Logout failed')
  }
}

const handleComponentError = (error) => {
  console.error('Component error:', error)
  toast.error('An error occurred while loading the page')
}

// Page transitions
const beforeEnter = (el) => {
  el.style.opacity = '0'
  el.style.transform = 'translateY(10px)'
}

const enter = (el, done) => {
  el.offsetHeight // force reflow
  el.style.transition = 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
  el.style.opacity = '1'
  el.style.transform = 'translateY(0)'
  setTimeout(done, 300)
}

const leave = (el, done) => {
  el.style.transition = 'all 0.2s cubic-bezier(0.7, 0, 0.84, 0)'
  el.style.opacity = '0'
  el.style.transform = 'translateY(-10px)'
  setTimeout(done, 200)
}

// Keyboard shortcuts
const handleKeydown = (event) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
    event.preventDefault()
    sidebarOpen.value = !sidebarOpen.value
  }
  
  if (event.key === 'Escape' && sidebarOpen.value) {
    sidebarOpen.value = false
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.page-content {
  width: 100%;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Custom scrollbar for main content */
main::-webkit-scrollbar {
  width: 6px;
}

main::-webkit-scrollbar-track {
  background: transparent;
}

main::-webkit-scrollbar-thumb {
  background: var(--surface-hover);
  border-radius: 10px;
}

main::-webkit-scrollbar-thumb:hover {
  background: var(--primary);
  opacity: 0.5;
}
</style>
